/**
 * @fileoverview The advice as a screen asks for it (the tips and notices,
 * placed by the server: the to-do rebuild, batch 5), and the season review's
 * own asks. The provider is TipsContext.tsx.
 *
 * Where: every screen that shows advice, says an event that finishes some,
 * or keeps one of the season's asks: Today (TopTip, TodayNotice, the welcome's
 * steps), the plant form, Add photos (PhotoTray), the packet sheet
 * (usePacketsFiled), ShareMoments and SeasonMoments.
 * API: none; the provider reads and marks.
 * Watch out: outside the provider there is no advice, every mark is a no-op
 * and askedOnce says true, so nothing pops up.
 *
 * @module tips/useTips
 */

import React from 'react';
import type { Advice, AdviceMark, AdvicePlace, TipEvent, TipMark, TipWords } from './types';

/** The React Query key the season asks' seen rows are cached under (GET /tips). */
export const TIPS_QUERY_KEY = ['tips'] as const;

/** The React Query key the advice before a postcode is cached under (GET /today/brief/reads). */
export const READS_QUERY_KEY = ['today-reads'] as const;

/** What a screen can do with the advice. */
export interface TipsApi {
  /** The one piece of advice the server placed here, or null (none, put away in this visit, or not in yet). */
  read: (place: AdvicePlace) => Advice | null;
  /** Its words with the moment's own put in ({plant}, {location}). */
  words: (advice: Advice, extra?: Record<string, string | null | undefined>) => TipWords;
  /** Says a piece of advice was seen (once a day), put away, or done (its button). */
  mark: (advice: Advice, mark: AdviceMark) => void;
  /** Something happened: the server finishes the advice waiting for it (only when the brief lists the event); gardenWritten: the write already invalidated the brief, so it is not invalidated again. */
  happened: (event: TipEvent, options?: { gardenWritten?: boolean }) => void;
  /**
   * Whether one of the season review's asks (not advice) has been made
   * already, under its key and year: done, put away, or shown on an earlier
   * visit. Kept with the tips' marks, so every device agrees.
   */
  askedOnce: (key: string, scope: number) => boolean;
  /** Marks one of the season review's asks shown (once per visit), done or put away. */
  markKey: (key: string, scope: number, mark: TipMark) => void;
  /** Whether the advice has arrived (or could not), so Today can choose its welcome step. */
  ready: boolean;
  /** Looking at someone else's garden; null until the season asks' marks are in (nothing is asked before then). */
  tending: boolean | null;
}

/** Outside the provider: no advice, and nothing to mark. */
const NONE: TipsApi = {
  read: () => null,
  words: () => ({ title: '', body: '', button: '' }),
  mark: () => {},
  happened: () => {},
  askedOnce: () => true,
  markKey: () => {},
  ready: false,
  tending: null,
};

/** The advice, given by TipsProvider. */
export const TipsContext = React.createContext<TipsApi>(NONE);

/**
 * The advice, for a screen to draw what the server placed and to mark it.
 *
 * @returns The provider's TipsApi (or NONE outside it).
 */
export function useTips(): TipsApi {
  return React.useContext(TipsContext);
}
